import { Button } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { trpc } from '../lib/trpc';
import type { GridColumn } from '../lib/types';

interface CsvExportProps {
  entity: string;
  columns: GridColumn[];
  filename?: string;
}

function buildCsv(columns: GridColumn[], rows: Record<string, unknown>[]): string {
  const headers = columns.map((col) => col.title);
  const headerLine = headers.map(escapeCsvCell).join(',');

  const dataLines = rows.map((row) =>
    columns.map((col) => {
      const val = row[col.key];
      if (val === null || val === undefined) return '';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      return escapeCsvCell(String(val));
    }).join(','),
  );

  return [headerLine, ...dataLines].join('\n');
}

function escapeCsvCell(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function CsvExport({ entity, columns, filename }: CsvExportProps) {
  const query = trpc.admin.crud.list.useQuery(
    { entity, page: 1, pageSize: 1000 },
    { enabled: false },
  );

  async function handleExport() {
    const result = await query.refetch();
    const rows = result.data?.rows ?? [];
    const csv = buildCsv(columns, rows);
    downloadCsv(csv, filename ?? `${entity}-export.csv`);
  }

  return (
    <Button
      variant="default"
      leftSection={<IconDownload size={16} />}
      onClick={handleExport}
      loading={query.isFetching}
    >
      Export CSV
    </Button>
  );
}
